import { api } from './client';
import type { SlaBadge } from './mrn';

export type RfqStatus = 'Draft' | 'Sent' | 'Quotes Received' | 'Closed' | 'Cancelled';

export interface RfqItem {
  id?: number;
  itemId?: number | null;
  itemName: string;
  qty: number | string;
  unit: string;
  targetPrice?: number | string | null;
}

export interface RfqVendorRow {
  rfqId: number;
  vendorId: number;
  invitedAt: string;
  vendor: { id: number; name: string; mobile?: string | null; whatsapp?: string | null };
}

export interface QuoteItem {
  id: number;
  rfqItemId: number;
  unitPrice: string;
  deliveryDays: number | null;
  remarks: string | null;
  aiScore: string | null;
  isBest: boolean;
}

export interface RfqQuote {
  id: number;
  rfqId: number;
  vendorId: number;
  submittedAt: string;
  validUntil: string | null;
  paymentTerms: string | null;
  remarks: string | null;
  vendor: { id: number; name: string };
  items: QuoteItem[];
}

export interface Rfq {
  id: number;
  rfqNo: string;
  rfqDate: string;
  prId: number | null;
  deadline: string | null;
  status: RfqStatus;
  remarks: string | null;
  items: RfqItem[];
  vendors: RfqVendorRow[];
  quotes?: RfqQuote[];
  quotesReceived?: number;
  vendorCount?: number;
  pr?: { prNo: string } | null;
  sla?: SlaBadge | null;
}

export interface RfqListResult {
  data: Rfq[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RfqFilter {
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface ManualQuotePayload {
  vendorId: number;
  validUntil?: string;
  paymentTerms?: string;
  remarks?: string;
  items: { rfqItemId: number; unitPrice: number; deliveryDays?: number; remarks?: string }[];
}

export async function listRfqs(filter: RfqFilter = {}): Promise<RfqListResult> {
  const r = await api.get<RfqListResult>('/rfq', { params: filter });
  return r.data;
}

export async function getRfq(id: number): Promise<Rfq> {
  const r = await api.get<Rfq>(`/rfq/${id}`);
  return r.data;
}

export async function createRfq(payload: {
  rfqDate: string;
  prId?: number;
  deadline?: string;
  vendorIds: number[];
  remarks?: string;
  items: Omit<RfqItem, 'id'>[];
}): Promise<Rfq> {
  const r = await api.post<Rfq>('/rfq', payload);
  return r.data;
}

export async function updateRfq(id: number, payload: Partial<Rfq> & { vendorIds?: number[] }): Promise<Rfq> {
  const r = await api.patch<Rfq>(`/rfq/${id}`, payload);
  return r.data;
}

export async function deleteRfq(id: number): Promise<void> {
  await api.delete(`/rfq/${id}`);
}

export async function sendRfq(id: number): Promise<Rfq> {
  const r = await api.post<Rfq>(`/rfq/${id}/send`);
  return r.data;
}

export async function generatePortalToken(rfqId: number, vendorId: number) {
  const r = await api.post<{ token: string; expiresAt: string }>(
    `/rfq/${rfqId}/portal-token/${vendorId}`,
  );
  return r.data;
}

export async function submitManualQuote(rfqId: number, payload: ManualQuotePayload): Promise<RfqQuote> {
  const r = await api.post<RfqQuote>(`/rfq/${rfqId}/manual-quote`, payload);
  return r.data;
}
